import { MultiValue } from "@common/data-models/complex-types/multi-value";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { Record } from "@common/data-models/records/record";
import { GroupKey } from "@common/data-models/search-results/group-key";
import { UserStaffInfo } from "@common/data-models/service/user-staff-info";
import { InvitationStatusCodes, GlobalRoles } from "@common/utils/util";

import { IGlobalTablesService } from "@common/interfaces/i-global-tables-service";
import { INomWebServicesJson } from "@common/interfaces/i-nom-web-services-json";
import { LookupList } from "@suite/common/data-models/complex-types/lookup-list";
import { group } from "@angular/animations";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ObjectTypes } from "@common/enums/object-types";
import { ViewStateOrder } from '../complex-types/view-state-order';
import { LayoutState } from '@suite/common/services/layout-state';
import { DateRange } from '../complex-types/date-range';
import { TopicViewStateRecord } from '../records/topic-viewstate-record';
import { ViewStateLayouts } from '@suite/common/enums/viewstate-layouts';
import moment from 'moment-timezone';

export class ResultGroupStaticServices {
    public static globalTablesService: IGlobalTablesService;
    public static nomWebServicesJson: INomWebServicesJson;
    public static viewStateService: LayoutState;
}

export type GroupValueConvertor = (rec: Record) => Array<GroupKey>;
export class GroupValueHelpers {
    public static MultiValueOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: Record) => {
            let groupVal = rec.getAs<MultiValue<string>>(fieldName, new MultiValue<string>());
            if (groupVal) {
                if (groupVal.length == 0) {
                    return [{ display: defaultValue, value: null }];
                } else {
                    let ret = new Array<{ display: string, value: string }>();
                    for (let key of groupVal) {
                        try {
                            if (!Object.keys(ResultGroupStaticServices.globalTablesService.idToDescriptionMap).length) {
                                ResultGroupStaticServices.globalTablesService.getListItemsForDesk(rec.type);
                            }
                        } catch (e) { }

                        let description = ResultGroupStaticServices.globalTablesService.idToDescriptionMap[key];

                        if (!description) {
                            description = key;
                        }
                        ret.push({ display: description, value: key });
                    }
                    return ret;
                }
            }
            return [{ display: defaultValue, value: null }];
        };
    }

    public static MultiDay(pFieldName: string): GroupValueConvertor {
        let fieldName = pFieldName;

        return (rec: Record) => {
            let groupVal = rec.getAs<DateRange>(fieldName);
            if (groupVal.from || groupVal.to) {
                let itemRangeStart = groupVal.from;
                let itemRangeEnd = groupVal.to;
                let currentDate = new Date;
                let rangeEndFormat = moment(itemRangeEnd.toUTCString());
                let rangeStartFormat = moment(itemRangeStart.toUTCString());
                let diff = rangeEndFormat.diff(rangeStartFormat, 'days') + 1;
                if (groupVal.get()) {
                    try {
                        let ret = new Array<{ display: string, value: string }>();
                        let rangeStart = new Date(moment(currentDate).startOf('isoWeek').add(ResultGroupStaticServices.nomWebServicesJson.HelloResults.DayOfWeek, 'days').subtract(1, 'day').toDate());
                        let rangeEnd = new Date(moment(currentDate).endOf('isoWeek').subtract(1, 'second').toDate());

                        for (let j = 0; j < diff; j++) {
                            let formatter = new Intl.DateTimeFormat("en-us", { month: "long" }); //Intl.DateTimeFormat() throws error only when "(UTC +01:00) Sao Tome" is set.

                            let topicDate = moment(itemRangeStart.toUTCString()).add(j, 'days').toDate();

                            if (ResultGroupStaticServices.globalTablesService.PersonalTimeZone && ResultGroupStaticServices.globalTablesService.ClientTimeZone && ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id != ResultGroupStaticServices.globalTablesService.ClientTimeZone.id) {
                                topicDate = DateTime.convertToTargetTZ(topicDate, ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id, ResultGroupStaticServices.globalTablesService.ClientTimeZone.id);
                            }
                            let groupValDisplay = formatter.format(topicDate).toString() + " " + topicDate.getDate().toString() + ", " + topicDate.getFullYear().toString();
                            let v = DateTime.handleDate(topicDate);
                            if (topicDate >= rangeStart && (<TopicViewStateRecord>ResultGroupStaticServices.viewStateService.ViewStateRecord).viewStateLayout.Value == ViewStateLayouts.CARD) {
                                ret.push({ display: groupValDisplay, value: v });
                            } else if (topicDate >= rangeStart && topicDate <= rangeEnd) {
                                ret.push({ display: groupValDisplay, value: v });
                            }
                        }
                        return ret;

                    } catch (err) {
                        return [{ display: "No Value", value: "" }];
                    }
                }
                else {
                    return [{ display: "No Value", value: "" }];
                }
            }
        };
    }

    public static LookupListOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: Record) => {
            let groupVal = rec.getAs<LookupList>(fieldName);
            if (groupVal) {
                if (groupVal.keys.length == 0) {
                    return [{ display: defaultValue, value: null }];
                } else {
                    let ret = new Array<{ display: string, value: string }>();
                    for (let item of groupVal) {

                        let description = item.displayText;
                        if (!description) {
                            description = item.id;
                        }
                        ret.push({ display: description, value: item.id });
                    }
                    return ret;
                }
            }
            return [{ display: defaultValue, value: null }];
        };
    }

    public static StringOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: Record) => {
            let key = rec.getAs<string>(fieldName);
            let type = rec.type;
            if (!key) key = defaultValue;
            if (fieldName == PSTFieldNames.PSTWirePriority && type == ObjectTypes.WIRE) {
                key = "WIRE_PRIORITY_" + key;
            }
            let description = ResultGroupStaticServices.globalTablesService.idToDescriptionMap[key];
            if (!description) {
                description = key;
            }
            return [{ display: description, value: key }];
        };
    }

    //TODO: remove this one and fix StringOrDefault
    public static StringOrDefaultWire(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: Record) => {
            let key = fieldName;
            let type = rec.type;
            if (!key) key = defaultValue;
            if (fieldName == PSTFieldNames.PSTWirePriority && type == ObjectTypes.WIRE) {
                key = "WIRE_PRIORITY_" + key;
            }
            let description = ResultGroupStaticServices.globalTablesService.idToDescriptionMap[key];
            if (!description) {
                description = key;
            }
            return [{ display: description, value: key }];
        };
    }

    public static StringOrTweetWire(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: Record) => {
            let key = fieldName;
            // let type = rec.type;
            return [{ display: rec.data[key], value: rec.data[key] }];
        };
    }

    public static Boolean(pFieldName: string): GroupValueConvertor {
        let fieldName = pFieldName;
        return (rec: Record) => {
            let groupVal = rec.getAs<boolean>(fieldName);
            if (groupVal === true) return [{ display: ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebYesOption, value: "1" }];
            if (groupVal === false) return [{ display: ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebNoOption, value: "0" }];
            return [{ display: "No Value", value: "" }];
        };
    }

    public static SingleGroup(): GroupValueConvertor {
        return (rec: Record) => {
            return [{ display: "Single Group", value: "SingleGroup" }];
        };
    }

    public static DateTime(pFieldName: string): GroupValueConvertor {
        let fieldName = pFieldName;
        return (rec: Record) => {
            let groupVal = rec.getAs<DateTime>(fieldName);
            if (!groupVal) groupVal = new DateTime();
            if (groupVal.get() && !(groupVal.get().getUTCFullYear() == 1800)) {
                try {
                    let formatter = new Intl.DateTimeFormat("en-us", { month: "long" }); //Intl.DateTimeFormat() throws error only when "(UTC +01:00) Sao Tome" is set.

                    let dateVal = new Date(groupVal.moment.toDate());

                    if (ResultGroupStaticServices.globalTablesService.PersonalTimeZone && ResultGroupStaticServices.globalTablesService.ClientTimeZone && ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id != ResultGroupStaticServices.globalTablesService.ClientTimeZone.id) {
                        dateVal = DateTime.convertToTargetTZ(dateVal, ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id, ResultGroupStaticServices.globalTablesService.ClientTimeZone.id);
                    }
                    let groupValDisplay = formatter.format(dateVal).toString() + " " + dateVal.getDate().toString() + ", " + dateVal.getFullYear().toString();
                    let v = DateTime.handleDate(dateVal);
                    return [{ display: groupValDisplay, value: v }];
                } catch (err) {
                    return [{ display: "No Value", value: "" }];
                }
            }
            else {
                return [{ display: "No Value", value: "" }];
            }
        };
    }

    public static Time(pFieldName: string): GroupValueConvertor {
        let fieldName = pFieldName;
        return (rec: Record) => {
            let groupVal = rec.getAs<DateTime>(fieldName);
            if (!groupVal) groupVal = new DateTime();
            if (groupVal.get() && !(groupVal.get().getUTCFullYear() == 1800)) {
                try {
                    let groupValDisplay = groupVal.moment.format('hA, MMM DD, YYYY');
                    let dateVal = new Date(groupVal.get().getFullYear(), groupVal.get().getMonth(), groupVal.get().getDate(), groupVal.get().getHours());
                    return [{ display: groupValDisplay, value: dateVal.toISOString() }];
                } catch (err) {
                    return [{ display: "No Value", value: "" }];
                }
            }
            else {
                return [{ display: "No Value", value: "" }];
            }
        };
    }

    public static StaffOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: Record) => {
            let val = rec.getAs<MultiValue<string>>(fieldName, new MultiValue<string>());
            let staffIds: Array<GroupKey> = [];
            if (val && val.length > 0) {
                for (let key of val) {
                    let staff = ResultGroupStaticServices.globalTablesService.Staff[key];
                    let groupVal = staff ? staff.GivenName + " " + staff.Surname : defaultValue;
                    staffIds.push(new GroupKey(staff.UserID, groupVal));
                }
            } else {
                staffIds.push(new GroupKey(defaultValue, defaultValue));
            }
            return staffIds;
        };
    }

    public static RoleOrDefault(pType: string, pDefaultValue: string): GroupValueConvertor {
        let defaultValue = pDefaultValue;
        let type = pType;
        return (rec: Record) => {
            let roles: Array<GroupKey> = [];
            for (let role of ResultGroupStaticServices.globalTablesService.getRolePropertiesFor(type)) {
                let recProp = rec.getAs<MultiValue<string>>(role.ID, new MultiValue<string>());
                if (recProp && ((recProp.hasKey(ResultGroupStaticServices.nomWebServicesJson.HelloResults.UserID)) || (recProp.hasKey(role.ID + "." + ResultGroupStaticServices.nomWebServicesJson.HelloResults.UserID)))) {
                    roles.push(new GroupKey(role.ID, role.DefaultCaption));
                }
            }
            if (roles.length == 0) roles.push(new GroupKey(defaultValue, defaultValue));
            return roles;
        };
    }
}

export type GroupKeyComparator = (a: GroupKey, b: GroupKey) => number;
export type GroupKeyComparatorFunc = (sortOrder: string) => GroupKeyComparator;

export class GroupKeyComparatorHelpers {

    public static DisplayValue(): GroupKeyComparatorFunc {
        return (sortOrder: string) => {
            return (a: GroupKey, b: GroupKey) => {
                if (a.display > b.display) return sortOrder == "asc" ? 1 : -1;
                else if (a.display < b.display) return sortOrder == "asc" ? -1 : 1;
                else return 0;
            }
        };
    }

    public static Value(): GroupKeyComparatorFunc {
        return (sortOrder: string) => {
            return (a: GroupKey, b: GroupKey) => {
                if (a.value > b.value) return sortOrder == "asc" ? 1 : -1;
                else if (a.value < b.value) return sortOrder == "asc" ? -1 : 1;
                else return 0;
            }
        };
    }

    public static SingleGroup(): GroupKeyComparatorFunc {
        return (sortOrder: string) => {
            return (a: GroupKey, b: GroupKey) => {
                return 0;
            }
        };
    }

    public static ValueDesc(): GroupKeyComparator {
        return (a: GroupKey, b: GroupKey) => {
            if (a.value > b.value) return -1;
            else if (a.value < b.value) return 1;
            else return 0;
        };
    }

    public static DateValue(): GroupKeyComparatorFunc {
        return (sortOrder: string) => {
            return (a: GroupKey, b: GroupKey) => {
                let aValue: Date = new Date(a.value);
                let bValue: Date = new Date(b.value);

                if (b.value && !a.value) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!b.value && a.value) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!b.value && !a.value) {
                    return 0;
                }
                return sortOrder == "asc" ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }
        };
    }

    private static NaturalSort(s: string): string {
        return (' ' + s + ' ').replace(/[\s]+/g, ' ').toLowerCase().replace(/[\d]+/, function (d) {
            d = '' + 1e20 + d;
            return d.substring(d.length - 20);
        });
    }

    public static DisplayValueDefaultBottom(pDefaultValue: string): GroupKeyComparatorFunc {
        return (sortOrder: string) => {
            let defaultValue = pDefaultValue;
            return (a: GroupKey, b: GroupKey) => {
                if ((a.display == defaultValue) && (b.display == defaultValue)) {
                    return 0;
                } else if ((a.display == defaultValue) && (b.display != defaultValue)) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if ((a.display != defaultValue) && (b.display == defaultValue)) {
                    return sortOrder == "asc" ? -1 : 1;
                } else {
                    let aValue = GroupKeyComparatorHelpers.NaturalSort(a.display);
                    let bValue = GroupKeyComparatorHelpers.NaturalSort(b.display);

                    let oRes = aValue.localeCompare(bValue);
                    if (oRes > 0) return sortOrder == "asc" ? 1 : -1;
                    else if (oRes < 0) return sortOrder == "asc" ? -1 : 1;
                    else return 0;
                }
            }
        };
    }
}

export type RecordComparator = (a: Record, b: Record) => number;
export type RecordComparatorFunc = (sortOrder: string) => RecordComparator;

export class RecordComparatorHelpers {

    private static NaturalSort(s: string): string {
        return (' ' + s + ' ').replace(/[\s]+/g, ' ').toLowerCase().replace(/[\d]+/, function (d) {
            d = '' + 1e20 + d;
            return d.substring(d.length - 20);
        });
    }

    private static GroupByParent(a: Record, b: Record, sortOrder: string, groupByParent: boolean): number {
        if (groupByParent && !!a.parentRecord && !!b.parentRecord && a.parentRecord.guid && b.parentRecord.guid) {
            let aValue: string = a.parentRecord.guid;
            let bValue: string = b.parentRecord.guid;

            let oRes = aValue.localeCompare(bValue);
            if (oRes > 0) return sortOrder == "asc" ? 1 : -1;
            else if (oRes < 0) return sortOrder == "asc" ? -1 : 1;
            else return 0;
        }
        return 0;
    }

    public static Generic(pFieldName: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: Record, b: Record) => {
                let aValue: string = a.get(fieldName);
                let bValue: string = b.get(fieldName);

                aValue = RecordComparatorHelpers.NaturalSort(aValue);
                bValue = RecordComparatorHelpers.NaturalSort(bValue);

                let oRes = aValue.localeCompare(bValue);
                if (oRes > 0) return sortOrder == "asc" ? 1 : -1;
                else if (oRes < 0) return sortOrder == "asc" ? -1 : 1;
                else return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
            }
        };
    }

    private static boolComparable(val: boolean): number {
        let valNorm = null
        if (val === true) valNorm = 3;
        else if (val === false) valNorm = 2;
        else valNorm = 1;
        return valNorm
    }

    public static Boolean(pFieldName: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: Record, b: Record) => {
                let aValue = RecordComparatorHelpers.boolComparable(a.get(fieldName));
                let bValue = RecordComparatorHelpers.boolComparable(b.get(fieldName));
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
            }
        };
    }

    public static Manual(): RecordComparatorFunc {
        return (sortOrder: string) => {
            return (a: Record, b: Record) => {
                let aValue: number = 0;
                let oATopicOrder = (<ViewStateOrder>a.get(PSTFieldNames.PSTTopicOrder, new ViewStateOrder()));
                if (oATopicOrder.hasValues()) {
                    aValue = oATopicOrder.get(ResultGroupStaticServices.viewStateService.ViewStateRecord.guid);
                }
                if (!aValue) {
                    let dateValue = a.get("CreatedDateTime");
                    if (dateValue) {
                        aValue = moment(dateValue.get()).unix() * 1000;
                    }
                }

                let bValue: number = 0;
                let oBTopicOrder = (<ViewStateOrder>b.get(PSTFieldNames.PSTTopicOrder, new ViewStateOrder()));
                if (oBTopicOrder.hasValues()) {
                    bValue = oBTopicOrder.get(ResultGroupStaticServices.viewStateService.ViewStateRecord.guid);
                }
                if (!bValue) {
                    let dateValue = b.get("CreatedDateTime");
                    if (dateValue) {
                        bValue = moment(dateValue.get()).unix() * 1000;
                    }
                }
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return 0;
            }
        };
    }

    public static String(pFieldName: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: Record, b: Record) => {
                let aValue: string = a.getAs<string>(fieldName);
                let bValue: string = b.getAs<string>(fieldName);
                if (bValue && !aValue) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bValue && aValue) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bValue && !aValue) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
                }
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
            }
        };
    }

    public static MultiValue(pFieldName: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: Record, b: Record) => {
                let aValue = null;
                let bValue = null;
                let aVal = a.getAs<MultiValue<string>>(fieldName, new MultiValue<string>());
                let bVal = b.getAs<MultiValue<string>>(fieldName, new MultiValue<string>());
                aValue = aVal[0]
                bValue = bVal[0]
                if (bValue && !aValue) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bValue && aValue) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bValue && !aValue) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
                }
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
            }
        };
    }

    public static LookupList(pFieldName: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: Record, b: Record) => {
                let aValue = null;
                let bValue = null;
                let aVal = a.getAs<LookupList>(fieldName);
                let bVal = b.getAs<LookupList>(fieldName);
                if (aVal && aVal.keys && aVal.keys.length > 0) aValue = aVal.keys[0]
                if (bVal && bVal.keys && bVal.keys.length > 0) bValue = bVal.keys[0]
                if (bValue && !aValue) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bValue && aValue) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bValue && !aValue) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
                }
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
            }
        };
    }

    public static DateTime(pFieldName: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: Record, b: Record) => {
                let aField = (a.getAs<DateTime>(fieldName) && a.getAs<DateTime>(fieldName).get) ? a.getAs<DateTime>(fieldName).deepCopy().get() : null;
                let bField = (b.getAs<DateTime>(fieldName) && b.getAs<DateTime>(fieldName).get) ? b.getAs<DateTime>(fieldName).deepCopy().get() : null;
                if (bField && !aField) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bField && aField) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bField && !aField) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
                }
                let timeDiff;
                if (sortOrder == "asc") timeDiff = aField.getTime() - bField.getTime();
                else timeDiff = bField.getTime() - aField.getTime();
                if (timeDiff == 0) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
                } else {
                    return timeDiff;
                };
            }
        };
    }

    public static StaffOrDefault(pFieldName: string, pDefaultValue: string, bGroupByParent: boolean = false): RecordComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            let defaultValue = pDefaultValue;
            let aString: string = "";
            let bString: string = "";

            return (a: Record, b: Record) => {
                aString = "";
                let val = a.getAs<MultiValue<string>>(fieldName);
                if (val?.length) {
                    let aStaff = ResultGroupStaticServices.globalTablesService.Staff[val[0]];
                    aString = aStaff ? aStaff.GivenName + " " + aStaff.Surname : defaultValue;
                }
                bString = "";
                val = b.getAs<MultiValue<string>>(fieldName);
                if (val?.length) {
                    let bStaff = ResultGroupStaticServices.globalTablesService.Staff[val[0]];
                    bString = bStaff ? bStaff.GivenName + " " + bStaff.Surname : defaultValue;
                }
                if (aString > bString) return sortOrder == "asc" ? 1 : -1;
                else if (aString < bString) return sortOrder == "asc" ? -1 : 1;
                else return RecordComparatorHelpers.GroupByParent(a, b, sortOrder, bGroupByParent);
            }
        };
    }

}

export class ResultGroupBase<T> {
    public entries: { [id: string]: T } = {};
    public groupDisplay: { [id: string]: string } = {};
    public payload: { [id: string]: any } = {};
    public keys: Array<GroupKey> = [];
    public isBuilt: boolean = false;

    public clear() {
        this.entries = {};
        this.groupDisplay = {};
        this.keys = [];
        this.isBuilt = false;
        this.payload = {};
    }
}

export class ResultGroup<T extends Record> extends ResultGroupBase<Array<T>> {
    public addToGroups(groupKeys: Array<GroupKey>, item: T) {
        if (groupKeys) {
            for (let groupKey of groupKeys) {
                this.groupDisplay[groupKey.value] = groupKey.display;
                this.payload[groupKey.value] = groupKey.payload;

                if (!this.entries[groupKey.value]) {
                    this.entries[groupKey.value] = [];
                    this.keys.push(groupKey);
                }
                this.entries[groupKey.value].push(item);
            }
        }
    }

    public insertAfterEntry(groupKeys: Array<GroupKey>, item: T, itemToInsert: T) {
        for (let groupKey of groupKeys) {
            if (this.entries[groupKey.value]) {
                let index = this.entries[groupKey.value].indexOf(item);
                this.entries[groupKey.value].splice(index + 1, 0, itemToInsert);
            }
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        let emptyGroupsToRemove: Array<string> = [];

        for (let key in this.entries) {
            let index = this.entries[key].indexOf(item);
            if (index >= 0) this.entries[key].splice(index, 1);
            if (this.entries[key].length == 0) emptyGroupsToRemove.push(key);
        }

        for (let emptyGroup of emptyGroupsToRemove) {
            delete this.entries[emptyGroup];
            delete this.groupDisplay[emptyGroup];
            let index = -1;
            for (let i = 0; i < this.keys.length; i++) {
                if (this.keys[i].value == emptyGroup) {
                    index = i;
                    break;
                }
            }
            if (index >= 0) this.keys.splice(index, 1);
        }
    }

    public sort(keyComparator: GroupKeyComparator, itemComparator: RecordComparator) {
        this.keys = [];
        for (let key of Object.keys(this.entries)) {
            this.keys.push(new GroupKey(key, this.groupDisplay[key], this.payload[key]));
        }
        this.keys.sort(keyComparator);
        for (let key of this.keys) {
            this.entries[key.value].sort(itemComparator);
        }
    }
}

export class FixedEntriesResultGroup<T extends Record> extends ResultGroup<T> {

    constructor(entries: Array<string>) {
        super();
        for (let key of entries) {
            this.entries[key] = [];
            let display = ResultGroupStaticServices.globalTablesService.idToDescriptionMap[key];
            if (!display) {
                display = key;
            }
            this.keys.push({ "display": display, "value": key });
            this.groupDisplay[key] = display;
        }
    }

    public addToGroups(groupKeys: Array<GroupKey>, item: T) {
        for (let groupKey of groupKeys) {
            if (this.entries[groupKey.value]) {
                this.groupDisplay[groupKey.value] = groupKey.display;
                this.payload[groupKey.value] = groupKey.payload;
                this.entries[groupKey.value].push(item);
            }
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        return;
    }
}

export class ResultGroupingDefinition {
    constructor(
        public valueConvertor?: GroupValueConvertor,
        public groupKeyComparator?: GroupKeyComparatorFunc,
        public recordComparator?: RecordComparatorFunc
    ) { }
}

export class ResultGroup2<T extends Record> extends ResultGroupBase<ResultGroup<T>> {
    public addToGroups(groupKeys: Array<GroupKey>, groupKeys2: Array<GroupKey>, item: T) {
        for (let groupKey of groupKeys) {
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
            if (!this.entries[groupKey.value]) {
                this.entries[groupKey.value] = new ResultGroup<T>();
                this.keys.push(groupKey);
            }
            this.entries[groupKey.value].addToGroups(groupKeys2, item);
        }
    }

    public insertAfterEntry(groupKeys: Array<GroupKey>, insertAfter: T, itemToInsert: T) {
        for (let groupKey of groupKeys) {
            let objGroupKey = this.entries[groupKey.value]
            if (objGroupKey) {
                let innerGroups = this.entries[groupKey.value]
                for (let innerGroupKey of innerGroups.keys) {
                    let index = innerGroups.entries[innerGroupKey.value].indexOf(insertAfter);
                    if (index > -1) {
                        innerGroups.entries[innerGroupKey.value].splice(index + 1, 0, itemToInsert);
                    }
                }
            }
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        let emptyGroupsToRemove: Array<string> = [];

        for (let key in this.entries) {
            this.entries[key].remove(groupKeys, item);
            if (Object.keys(this.entries[key].entries).length == 0) {
                emptyGroupsToRemove.push(key);
            }
        }

        for (let emptyGroup of emptyGroupsToRemove) {
            delete this.entries[emptyGroup];
            delete this.groupDisplay[emptyGroup];
            let index = -1;
            for (let i = 0; i < this.keys.length; i++) {
                if (this.keys[i].value == emptyGroup) {
                    index = i;
                    break;
                }
            }
            if (index >= 0) this.keys.splice(index, 1);
        }
    }

    public sort(keyComparator: GroupKeyComparator, keyComparator2: GroupKeyComparator, itemComparator: RecordComparator) {
        this.keys = [];
        for (let key of Object.keys(this.entries)) {
            this.keys.push(new GroupKey(key, this.groupDisplay[key], this.payload[key]));
        }
        this.keys.sort(keyComparator);
        for (let key of this.keys) {
            this.entries[key.value].sort(keyComparator2, itemComparator);
        }
    }

    public clear() {
        for (let key in this.entries) {
            this.entries[key].clear();
        }
        super.clear();
    }
}

export class ResultGrouping2Definition {
    constructor(
        public valueConvertor?: GroupValueConvertor,
        public groupKeyComparator?: GroupKeyComparatorFunc,
        public valueConvertor2?: GroupValueConvertor,
        public groupKeyComparator2?: GroupKeyComparatorFunc,
        public recordComparator?: RecordComparatorFunc
    ) { }
}

export type GroupUserValueConvertor = (rec: UserStaffInfo) => Array<GroupKey>;
export class GroupUserValueHelpers {
    public static StringOrDefault(pFieldName: string, pDefaultValue: string): GroupUserValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: UserStaffInfo) => {
            let groupVal = rec.getAs<string>(fieldName);
            if (!groupVal) groupVal = defaultValue;
            return [{ display: groupVal, value: groupVal }];
        };
    }

    public static UserInfoStatusStringOrDefault(pFieldName: string, pDefaultValue: string): GroupUserValueConvertor {
        let defaultValue = pDefaultValue;
        let fieldName = pFieldName;
        return (rec: UserStaffInfo) => {
            let status = rec.getAs<string>(fieldName);
            if (!status) status = defaultValue;
            let groupVal = '';
            let groupLabel = '';

            if (status == InvitationStatusCodes.ACTIVE) {
                groupLabel = ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebAllTeamMembersLabel;
            } else if (status == InvitationStatusCodes.PENDING) {
                groupLabel = ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebPendingTeamMembersLabel;
            } else {
                groupLabel = ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebNewTeamMembersLabel;
            }
            groupVal = status;

            return [{ display: groupLabel, value: groupVal }];
        };
    }
}
export class UserGroup<T extends UserStaffInfo> extends ResultGroupBase<Array<T>> {
    public static USERS_PER_PAGE: number = 20;
    public currentPageForGroup: { [id: string]: number } = {};

    public getCurrentPage(group: string): number {
        if (!this.currentPageForGroup[group]) this.currentPageForGroup[group] = 0;
        return this.currentPageForGroup[group];
    }

    public setCurrentPage(group: string, page: number) {
        this.currentPageForGroup[group] = page;
    }

    public getPagesCount(group: string): number {
        let ret = Math.floor(this.entries[group].length / UserGroup.USERS_PER_PAGE);
        if (this.entries[group].length % UserGroup.USERS_PER_PAGE) ret++;
        return ret;
    }

    public getItemsForCurrentPage(group: string): Array<T> {
        return this.entries[group].slice(UserGroup.USERS_PER_PAGE * (this.getCurrentPage(group)), UserGroup.USERS_PER_PAGE * (this.getCurrentPage(group) + 1));
    }

    public addToGroups(groupKeys: Array<GroupKey>, item: T) {
        for (let groupKey of groupKeys) {
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;

            if (!this.entries[groupKey.value]) this.entries[groupKey.value] = [];
            this.entries[groupKey.value].push(item);
        }
    }

    public insertAfterEntry(groupKeys: Array<GroupKey>, item: T, itemToInsert: T) {
        for (let groupKey of groupKeys) {
            let index = this.entries[groupKey.value].indexOf(item);
            this.entries[groupKey.value].splice(index + 1, 0, itemToInsert);
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        let emptyGroupsToRemove: Array<string> = [];

        for (let key in this.entries) {
            let index = this.entries[key].indexOf(item);
            if (index >= 0) this.entries[key].splice(index, 1);
            if (this.entries[key].length == 0) emptyGroupsToRemove.push(key);
        }

        for (let emptyGroup of emptyGroupsToRemove) {
            delete this.entries[emptyGroup];
            delete this.groupDisplay[emptyGroup];
            let index = -1;
            for (let i = 0; i < this.keys.length; i++) {
                if (this.keys[i].value == emptyGroup) {
                    index = i;
                    break;
                }
            }
            if (index >= 0) this.keys.splice(index, 1);
        }
    }

    public sort(keyComparator: GroupKeyComparator, itemComparator: UserComparator) {
        this.keys = [];
        for (let key of Object.keys(this.entries)) {
            this.keys.push(new GroupKey(key, this.groupDisplay[key], this.payload[key]));
        }
        this.keys.sort(keyComparator);
        for (let key of this.keys) {
            this.entries[key.value].sort(itemComparator);
        }
    }
}

export class UserGroupingDefinition {
    constructor(
        public valueConvertor?: GroupUserValueConvertor,
        public groupKeyComparator?: GroupKeyComparatorFunc,
        public userComparator?: UserComparatorFunc
    ) { }
}
export type UserComparator = (a: UserStaffInfo, b: UserStaffInfo) => number;
export type UserComparatorFunc = (sortOrder: string) => UserComparator;

export class UserComparatorHelpers {

    public static Generic(pFieldName: string): UserComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: UserStaffInfo, b: UserStaffInfo) => {
                let aValue = a.get(fieldName);
                let bValue = b.get(fieldName);
                if (bValue && !aValue) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bValue && aValue) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bValue && !aValue) {
                    return 0;
                }
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return 0;
            }
        };
    }

    public static String(pFieldName: string): UserComparatorFunc {
        return (sortOrder: string) => {
            let fieldName = pFieldName;
            return (a: UserStaffInfo, b: UserStaffInfo) => {
                let aValue: string = a.getAs<string>(fieldName);
                let bValue: string = b.getAs<string>(fieldName);

                if (bValue && !aValue) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bValue && aValue) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bValue && !aValue) {
                    return 0;
                }
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
                if (aValue > bValue) return sortOrder == "asc" ? 1 : -1;
                else if (aValue < bValue) return sortOrder == "asc" ? -1 : 1;
                else return 0;
            }
        };
    }

    public static Role(): UserComparatorFunc {
        return (sortOrder: string) => {
            return (a: UserStaffInfo, b: UserStaffInfo) => {
                let aRoleDescription: string = null;
                let bRoleDescription: string = null;

                if (a.Supervisor) {
                    aRoleDescription = GlobalRoles.SUPERVISOR_ITEM.Description;
                } else {
                    let securityRole = ResultGroupStaticServices.globalTablesService.getSecurityRole(a.GlobalRole);
                    if (securityRole) aRoleDescription = securityRole["Description"];
                }

                if (b.Supervisor) {
                    bRoleDescription = GlobalRoles.SUPERVISOR_ITEM.Description;
                } else {
                    let securityRole = ResultGroupStaticServices.globalTablesService.getSecurityRole(b.GlobalRole);
                    if (securityRole) bRoleDescription = securityRole["Description"];
                }

                if (bRoleDescription && !aRoleDescription) {
                    return sortOrder == "asc" ? 1 : -1;
                } else if (!bRoleDescription && aRoleDescription) {
                    return sortOrder == "asc" ? -1 : 1;
                } else if (!bRoleDescription && !aRoleDescription) {
                    return 0;
                }
                aRoleDescription = aRoleDescription.toLowerCase();
                bRoleDescription = bRoleDescription.toLowerCase();
                if (aRoleDescription > bRoleDescription) return sortOrder == "asc" ? 1 : -1;
                else if (aRoleDescription < bRoleDescription) return sortOrder == "asc" ? -1 : 1;
                else return 0;
            }
        };
    }
}

